import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSeo } from '../../context/SeoContext';

/**
 * Reusable SEO component to set meta tags for different pages.
 * 
 * @param {Object} props
 * @param {string} props.pageKey - Key to map to SEO data from API (e.g. 'privateTours', 'hotels')
 * @param {string} [props.title] - Optional override for title
 * @param {string} [props.description] - Optional override for description
 * @param {string} [props.keywords] - Optional override for keywords
 * @param {boolean} [props.noindex] - Emit robots noindex for pages that must not be indexed
 */
const Seo = ({ pageKey, title, description, keywords, noindex }) => {
    const { seoData, loading } = useSeo();

    // Fallback values in case API data is missing or loading
    const defaultData = {
        title: 'Ayrarat Tours',
        description: 'Discover Armenia with Ayrarat Tours',
        keywords: 'Armenia, tours, travel, hotels'
    };

    const pageData = seoData && seoData[pageKey] ? seoData[pageKey] : {};

    const finalTitle = title || pageData.title || defaultData.title;
    const finalDescription = description || pageData.description || defaultData.description;
    const finalKeywords = keywords || pageData.keywords || defaultData.keywords;

    // Crawlers need absolute URLs; window is absent outside the browser
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const pageUrl = typeof window !== 'undefined' ? `${origin}${window.location.pathname}` : '';
    const imageUrl = origin ? `${origin}/og-image.jpg` : '';

    if (loading) return null;

    return (
        <Helmet>
            <title>{finalTitle}</title>
            <meta name="description" content={finalDescription} />
            <meta name="keywords" content={finalKeywords} />
            {noindex && <meta name="robots" content="noindex" />}
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={finalDescription} />
            {pageUrl && <meta property="og:url" content={pageUrl} />}
            {imageUrl && <meta property="og:image" content={imageUrl} />}
            
            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={finalDescription} />
            {imageUrl && <meta name="twitter:image" content={imageUrl} />}
        </Helmet>
    );
};

export default Seo;
